import { Alert, Box, Button, Card, CardContent, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { ItsOneViewResponse, ItsOneViewSectionKey } from "@/types/its-one-view";
import { navigateToBaseRoute } from "@/utils/route-utility";
import { SECTION_LABELS, isSabilSectionKey } from "../section-config";
import FinancialTable from "./FinancialTable";
import ReceiptsPanel from "./ReceiptsPanel";

type SectionPanelProps = {
  sectionKey: ItsOneViewSectionKey;
  sectionAccess: ItsOneViewResponse["sectionAccess"];
  sections: ItsOneViewResponse["sections"];
};

export default function SectionPanel({ sectionKey, sectionAccess, sections }: SectionPanelProps) {
  const access = sectionAccess[sectionKey];

  if (access === "denied") {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {SECTION_LABELS[sectionKey]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have permission to view this section.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const title = SECTION_LABELS[sectionKey];

  if (sectionKey === "receipts") {
    const block = sections.receipts;
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <ReceiptsPanel
            items={block?.items ?? []}
            accessibleSources={block?.accessibleSources ?? []}
          />
        </CardContent>
      </Card>
    );
  }

  if (sectionKey === "voluntary") {
    const block = sections.voluntary;
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {block?.sourceDescription ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {block.sourceDescription}
            </Alert>
          ) : null}
          <FinancialTable items={block?.items ?? []} />
        </CardContent>
      </Card>
    );
  }

  if (sectionKey === "yearlyLedger") {
    const block = sections.yearlyLedger;
    const items = block?.items ?? [];
    return (
      <Card variant="outlined">
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h6">{title}</Typography>
            <Button
              size="small"
              endIcon={<OpenInNewIcon />}
              onClick={() => navigateToBaseRoute("yearlyLedger")}
            >
              Open ledger module
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Miqaat Niyaaz & Tamiraat entries in yearly ledger linked to household ITS numbers.
          </Typography>
          <FinancialTable items={items} />
        </CardContent>
      </Card>
    );
  }

  const block = sections[sectionKey];
  const items = block?.items ?? [];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <FinancialTable items={items} showPaidTill={isSabilSectionKey(sectionKey)} />
      </CardContent>
    </Card>
  );
}
